import { LoyaltyCustomerModal, PageLayout, ThankYouModal } from 'components';
import LoyaltyJoinScreen from 'components/LoyaltyJoinScreen/LoyaltyJoinScreen';
import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import { useState } from 'react';
import { TCustomerStatus } from 'shared/api/v2/customers/customers.types';
import { getMerchantInfo } from 'shared/api/v2/merchants/merchants.data';
import {
    GetMerchantInfoResponse,
    MerchantType,
} from 'shared/api/v2/merchants/merchants.types';
import { REDIRECT_ROUTES } from 'shared/constants/redirectRouts';
import { useModalsState } from 'shared/hooks/useModalsState';
import { TSourceKey, TUTMMediumKey } from 'shared/types';
import { logger } from 'shared/utils';
import {
    TVerificationModalError,
    VerificationCodeModal,
} from '../../components/VerificationCodeModal/VerificationCodeModal';
import { getMerchantsPhoneNumber } from '../../shared/api/v2/customers/customers.utils';
import { faro } from '@grafana/faro-web-sdk';

type JoinLoyaltyPageProps = {
    merchantInfo: GetMerchantInfoResponse;
    merchantId: string;
    merchantVerificationPhoneNumber: string | null;
    source: TSourceKey;
    medium?: TUTMMediumKey | null;
};

export const getServerSideProps: GetServerSideProps<
    JoinLoyaltyPageProps
> = async (context) => {
    const merchantId = context.query?.merchantId as string;
    let medium: TUTMMediumKey | undefined;

    switch (context.query?.utm_medium) {
        case 'qr':
            medium = 0;
            break;
        case 'link':
            medium = 1;
            break;
    }

    // Always 5 = loyalty_signup_page
    const source = 5 as TSourceKey;

    if (!merchantId) {
        faro.api.pushError(
            new Error('SSR:join-loyalty: Missing merchantId from query params')
        );
        logger.warn(
            'Missing merchantId from query params',
            'SSR:join-loyalty',
            {
                url: context.req.url,
                query: context.query,
            }
        );
        return REDIRECT_ROUTES.invalid;
    }

    try {
        logger.info(
            'Fetching merchant info for loyalty signup',
            'SSR:join-loyalty',
            {
                merchantId,
                medium,
                url: context.req.url,
            }
        );

        const getMerchantInfoRequest = await getMerchantInfo(merchantId);
        const getMerchantInfoResponse = await getMerchantInfoRequest.json();
        const merchantsPhoneVerificationNumber =
            await getMerchantsPhoneNumber();

        if ('error' in getMerchantInfoResponse) {
            logger.warn(
                'Error in getMerchantInfo response',
                'SSR:join-loyalty',
                {
                    merchantId,
                    error: getMerchantInfoResponse.error,
                    url: context.req.url,
                }
            );
            return REDIRECT_ROUTES.unstarted;
        }

        if (getMerchantInfoResponse?.has_loyalty === false) {
            logger.warn(
                'Merchant does not have loyalty enabled',
                'SSR:join-loyalty',
                {
                    merchantId,
                    business_name: getMerchantInfoResponse.business_name,
                    url: context.req.url,
                }
            );
            return REDIRECT_ROUTES.invalid;
        }

        return {
            props: {
                merchantInfo: getMerchantInfoResponse,
                merchantId,
                merchantVerificationPhoneNumber:
                    merchantsPhoneVerificationNumber.data?.phone_number || null,
                source,
                medium: medium ?? null,
            },
        };
    } catch (err) {
        logger.error(
            'Exception while fetching merchant info',
            'SSR:join-loyalty',
            {
                merchantId,
                error: err instanceof Error ? err.message : String(err),
                stack: err instanceof Error ? err.stack : undefined,
                url: context.req.url,
            }
        );
        return REDIRECT_ROUTES.invalid;
    }
};

const JoinLoyaltyPage: React.FC<
    InferGetServerSidePropsType<typeof getServerSideProps>
> = ({
    merchantInfo,
    merchantId,
    merchantVerificationPhoneNumber,
    source,
    medium,
}) => {
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [customerId, setCustomerId] = useState<string | null>(null);
    const [customerStatus, setCustomerStatus] = useState<TCustomerStatus>();
    const [isVerificationModalOpen, setIsVerificationModalOpen] =
        useState(false);
    const [verificationModalInitialError, setVerificationModalInitialError] =
        useState<TVerificationModalError | null>(null);
    const [
        shouldShowCustomerModalAfterClose,
        setShouldShowCustomerModalAfterClose,
    ] = useState(false);

    const {
        isCustomerModalOpen,
        isThankYouOpen,
        setIsCustomerModalOpen,
        setIsThankYouOpen,
    } = useModalsState();

    const handleVerificationModalOpen = (
        isOpen: boolean,
        shouldShowCustomerModalAfterClose?: boolean,
        errorMessage?: TVerificationModalError
    ) => {
        setIsVerificationModalOpen(isOpen);
        if (shouldShowCustomerModalAfterClose) {
            setShouldShowCustomerModalAfterClose(true);
        }
        if (errorMessage) {
            setVerificationModalInitialError(errorMessage);
        }
    };

    const handleVerificationModalClose = () => {
        setIsVerificationModalOpen(false);
        if (shouldShowCustomerModalAfterClose) {
            setIsCustomerModalOpen(true);
            setShouldShowCustomerModalAfterClose(false);
        }
    };

    return (
        <PageLayout merchantInfo={merchantInfo}>
            <div id="modalroot">
                <LoyaltyJoinScreen
                    businessName={merchantInfo.business_name}
                    customerEmail={email}
                    customerPhoneNumber={phoneNumber}
                    setCustomerEmail={setEmail}
                    setCustomerPhoneNumber={setPhoneNumber}
                    setCustomerId={setCustomerId}
                    setCustomerStatus={setCustomerStatus}
                    isVerificationModalOpen={isVerificationModalOpen}
                    setIsVerificationModalOpen={handleVerificationModalOpen}
                    isRootMerchant={merchantInfo.type === MerchantType.ROOT}
                    setIsCustomerModalOpen={setIsCustomerModalOpen}
                    setIsThankYouOpen={setIsThankYouOpen}
                    merchantId={merchantId}
                    merchantPhoneNumber={merchantVerificationPhoneNumber}
                    source={source}
                    spotsReward={merchantInfo.spots_reward}
                    spotsSource={merchantInfo.spots_source}
                    isExpressEnabled={merchantInfo?.has_express ?? false}
                    medium={medium ?? 1}
                    spotsMinPurchaseAmount={merchantInfo.purchase_amount}
                />
                <LoyaltyCustomerModal
                    isOpen={isCustomerModalOpen}
                    setIsOpen={setIsCustomerModalOpen}
                    isRootMerchant={merchantInfo.type === MerchantType.ROOT}
                    customerEmail={email}
                    customerId={customerId}
                    customerStatus={customerStatus}
                    source={source}
                    merchantLocations={merchantInfo?.locations ?? []}
                    setIsThankYouOpen={setIsThankYouOpen}
                    customerPhoneNumber={phoneNumber}
                    medium={medium ?? 1}
                />
                <ThankYouModal
                    isOpen={isThankYouOpen}
                    setIsOpen={setIsThankYouOpen}
                    isLoyalty
                />
                <VerificationCodeModal
                    customerId={customerId}
                    customerPhoneNumber={phoneNumber}
                    isOpen={isVerificationModalOpen}
                    initialError={verificationModalInitialError || undefined}
                    onConfirm={() => setIsCustomerModalOpen(true)}
                    onClose={handleVerificationModalClose}
                />
            </div>
        </PageLayout>
    );
};

export default JoinLoyaltyPage;
